import { TranslationKey } from "../i18n/translation-key"
import { PartFunction } from "./part-function"
import { ServiceAction, ServicePartActionUtils } from "./service-part-action"

export enum PartGrade {
  ZERO = 0,
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
  SIX = 6,
  SEVEN = 7,
  EIGHT = 8,
  NINE = 9,
  TEN = 10,
}

interface PartGradeLabelKeys {
  short: TranslationKey
  verbose: TranslationKey
}

const PART_GRADE_LABEL_KEYS: Partial<Record<PartGrade, PartGradeLabelKeys>> = {
  [PartGrade.FOUR]: {
    short: "common.partGrade.broken.short",
    verbose: "common.partGrade.broken.verbose",
  },
  [PartGrade.FIVE]: {
    short: "common.partGrade.zeroToThreeMonths.short",
    verbose: "common.partGrade.zeroToThreeMonths.verbose",
  },
  [PartGrade.SIX]: {
    short: "common.partGrade.threeToSixMonths.short",
    verbose: "common.partGrade.threeToSixMonths.verbose",
  },
  [PartGrade.SEVEN]: {
    short: "common.partGrade.sixToTwelveMonths.short",
    verbose: "common.partGrade.sixToTwelveMonths.verbose",
  },
  [PartGrade.EIGHT]: {
    short: "common.partGrade.twelvePlusMonths.short",
    verbose: "common.partGrade.twelvePlusMonths.verbose",
  },
  [PartGrade.NINE]: {
    short: "common.partGrade.perfect.short",
    verbose: "common.partGrade.perfect.verbose",
  },
  [PartGrade.TEN]: {
    short: "common.partGrade.new.short",
    verbose: "common.partGrade.new.verbose",
  },
}

export class PartGradeUtils {
  /**
   * The action an inspection fills in for a grade until the mechanic picks
   * one: a component with 30 % or less of its lifetime left is changed, any
   * other is left alone.
   */
  static suggestedAction(grade: PartGrade): ServiceAction {
    if (grade <= PartGrade.THREE) return ServiceAction.CHANGE
    return ServiceAction.DO_NOTHING
  }

  /**
   * The function an inspection fills in for a grade: a component with 40 % or
   * less of its lifetime left doesn't work optimally, one with nothing left
   * doesn't work at all.
   */
  static suggestedPartFunction(grade: PartGrade | null): PartFunction {
    if (grade === PartGrade.ZERO) return PartFunction.NOT_WORKING
    if (grade !== null && grade <= PartGrade.FOUR) {
      return PartFunction.NOT_OPTIMAL
    }
    return PartFunction.WORKS
  }

  /**
   * The function after the grade changes. It follows the grade until the
   * mechanic picks one themselves, like "Fungerar inte optimalt" on a nearly
   * new derailleur. The pick isn't saved, so a reopened draft still keeps a
   * function that differs from its grade's suggestion. A component to change
   * doesn't work, whatever its grade.
   */
  static partFunctionAfterGradeChange(part: {
    previousGrade: PartGrade | null
    newGrade: PartGrade
    partFunction: PartFunction
    pickedPartFunction: PartFunction | null
    recommendedAction: ServiceAction | null
  }): PartFunction {
    if (part.recommendedAction === ServiceAction.CHANGE) {
      return PartFunction.NOT_WORKING
    }
    if (part.pickedPartFunction !== null) return part.pickedPartFunction
    const isSuggested =
      part.partFunction ===
      PartGradeUtils.suggestedPartFunction(part.previousGrade)
    if (!isSuggested) return part.partFunction
    return PartGradeUtils.suggestedPartFunction(part.newGrade)
  }

  /**
   * The function after the action changes. "Byt" always means the component
   * doesn't work. Moving away from "Byt" gives back the function the mechanic
   * picked, or the grade's suggestion if they never picked one.
   */
  static partFunctionAfterActionChange(part: {
    previousAction: ServiceAction | null
    newAction: ServiceAction | null
    grade: PartGrade | null
    partFunction: PartFunction
    pickedPartFunction: PartFunction | null
  }): PartFunction {
    if (part.newAction === ServiceAction.CHANGE) return PartFunction.NOT_WORKING
    if (part.previousAction !== ServiceAction.CHANGE) return part.partFunction
    return (
      part.pickedPartFunction ??
      PartGradeUtils.suggestedPartFunction(part.grade)
    )
  }

  /**
   * Whether a component with this action can be saved without a grade. Work
   * that only looks at the component says nothing about its wear, and "Montera"
   * has no component on the bike to grade yet.
   */
  static isGradeOptional(action: ServiceAction): boolean {
    return (
      action === ServiceAction.ADJUST ||
      action === ServiceAction.CHECK ||
      action === ServiceAction.TROUBLESHOOT ||
      ServicePartActionUtils.addsNewComponent(action)
    )
  }

  /**
   * The grade an inspection fills in when the mechanic (or a package) picks
   * an action on an ungraded component. A component to change is nearly worn
   * out, so it defaults to "Trafiksäker"; the others that need a grade default
   * to "Toppskick". Null when the action needs no grade: the mechanic sets one
   * themselves if they want one.
   */
  static suggestedGrade(action: ServiceAction): PartGrade | null {
    if (PartGradeUtils.isGradeOptional(action)) return null
    if (action === ServiceAction.DO_NOTHING) return PartGrade.EIGHT
    if (action === ServiceAction.CHANGE) return PartGrade.ONE
    return PartGrade.SIX
  }

  /**
   * The grade once the work is done. "Byt" and "Montera" leave a new component
   * on the bike; any other work leaves `gradeOtherwise`, which differs between
   * the flows that create work.
   */
  static gradeAfterAction<T extends PartGrade | null>(
    action: ServiceAction,
    gradeOtherwise: T,
  ): PartGrade | T {
    if (action === ServiceAction.CHANGE || action === ServiceAction.MOUNT) {
      return PartGrade.TEN
    }
    return gradeOtherwise
  }

  /**
   * Whether the mechanic evaluated the component: it has an action and, unless
   * the action needs none, a grade. Only evaluated components are saved with
   * the inspection.
   */
  static isEvaluated<
    T extends {
      grade: PartGrade | null
      recommendedAction: ServiceAction | null
    },
  >(part: T): part is T & { recommendedAction: ServiceAction } {
    if (part.recommendedAction === null) return false
    return (
      part.grade !== null ||
      PartGradeUtils.isGradeOptional(part.recommendedAction)
    )
  }

  /**
   * Whether the mechanic judged the component themselves. Grades set for every
   * component at once say nothing about it, and neither does a package's work
   * until the mechanic grades it.
   */
  static countsTowardsQuality(
    part: { id: string; grade: PartGrade | null; isMassGraded: boolean },
    packagePartIds: string[],
  ): boolean {
    if (part.isMassGraded) return false
    return part.grade !== null || !packagePartIds.includes(part.id)
  }

  /**
   * How many components count towards the inspection's quality if submitted
   * right now.
   */
  static countEvaluated(
    parts: {
      id: string
      grade: PartGrade | null
      recommendedAction: ServiceAction | null
      isMassGraded: boolean
    }[],
    removedPartIds: string[],
    packagePartIds: string[] = [],
  ): number {
    return parts.filter(
      (part) =>
        PartGradeUtils.isEvaluated(part) &&
        PartGradeUtils.countsTowardsQuality(part, packagePartIds) &&
        !removedPartIds.includes(part.id),
    ).length
  }

  /**
   * Whether the component has an action that needs a grade the mechanic hasn't
   * set yet, e.g. work agreed at the reception.
   */
  static hasActionWithoutGrade(part: {
    grade: PartGrade | null
    recommendedAction: ServiceAction | null
  }): boolean {
    return (
      part.grade === null &&
      part.recommendedAction !== null &&
      !PartGradeUtils.isGradeOptional(part.recommendedAction)
    )
  }

  /** Whether the component has a grade or a grade-needing action, but not both. */
  static isHalfEvaluated(part: {
    grade: PartGrade | null
    recommendedAction: ServiceAction | null
  }): boolean {
    if (part.recommendedAction === null) return part.grade !== null
    return PartGradeUtils.hasActionWithoutGrade(part)
  }

  static toLabelKeys(grade: PartGrade): PartGradeLabelKeys | undefined {
    return PART_GRADE_LABEL_KEYS[grade]
  }

  static getGradeDashedColor(grade: number): string {
    const colors: Record<number, string> = {
      0: "text-grade-0",
      1: "text-grade-1",
      2: "text-grade-2",
      3: "text-grade-3",
      4: "text-grade-4",
      5: "text-grade-5",
      6: "text-grade-6",
      7: "text-grade-7",
      8: "text-grade-8",
      9: "text-grade-9",
      10: "text-grade-10",
    }
    return colors[Math.max(0, Math.min(10, grade))] ?? "text-foreground/30"
  }

  /** Solid fill for anything that shows a grade: bars and the selected step. */
  static getGradeBarClass(grade: number): string {
    const colors: Record<number, string> = {
      0: "bg-grade-0",
      1: "bg-grade-1",
      2: "bg-grade-2",
      3: "bg-grade-3",
      4: "bg-grade-4",
      5: "bg-grade-5",
      6: "bg-grade-6",
      7: "bg-grade-7",
      8: "bg-grade-8",
      9: "bg-grade-9",
      10: "bg-grade-10",
    }
    return colors[Math.max(0, Math.min(10, grade))] ?? "bg-secondary"
  }

  /** Pale background of the unselected steps in the grade slider. */
  static getGradeTintClass(grade: number): string {
    const colors: Record<number, string> = {
      0: "bg-grade-tint-0",
      1: "bg-grade-tint-1",
      2: "bg-grade-tint-2",
      3: "bg-grade-tint-3",
      4: "bg-grade-tint-4",
      5: "bg-grade-tint-5",
      6: "bg-grade-tint-6",
      7: "bg-grade-tint-7",
      8: "bg-grade-tint-8",
      9: "bg-grade-tint-9",
      10: "bg-grade-tint-10",
    }
    return colors[Math.max(0, Math.min(10, grade))] ?? "bg-background"
  }

  /** Part card tint, so the card and its grade slider read as one. */
  static getGradeCardClass(grade: number): string {
    const colors: Record<number, string> = {
      0: "bg-grade-tint-0 border-grade-0/40",
      1: "bg-grade-tint-1 border-grade-1/40",
      2: "bg-grade-tint-2 border-grade-2/40",
      3: "bg-grade-tint-3 border-grade-3/40",
      4: "bg-grade-tint-4 border-grade-4/40",
      5: "bg-grade-tint-5 border-grade-5/40",
      6: "bg-grade-tint-6 border-grade-6/40",
      7: "bg-grade-tint-7 border-grade-7/40",
      8: "bg-grade-tint-8 border-grade-8/40",
      9: "bg-grade-tint-9 border-grade-9/40",
      10: "bg-grade-tint-10 border-grade-10/40",
    }
    return colors[Math.max(0, Math.min(10, grade))] ?? "bg-background"
  }

  static getClassName(grade: PartGrade): string {
    return `${PartGradeUtils.getGradeBarClass(grade)} text-foreground`
  }
}
